import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { PeriodoDataCalendar } from '../components/PeriodoDataCalendar';
import { useRefetchWhenCatalogChanges } from '../contexts/AdminCatalogContext';
import { useAdminUnsaved } from '../contexts/AdminUnsavedContext';
import { useAdminDialog } from '../contexts/AdminDialogContext';
import { useOnIcalSynced } from '../contexts/IcalAutoSyncContext';
import {
  addHospedagem,
  getHospedagens,
  getHospedagemById,
  saveHospedagem,
  removeHospedagem,
  isHospedagemAtiva,
  TIPOS_HOSPEDAGEM,
  COMODIDADES_LIST,
  type Hospedagem,
  type HospedagemLayoutType,
  type TipoHospedagem,
  type TipoQuarto,
  type ICalUrlItem,
} from '../lib/hospedagemStorage';
import { getCidades } from '../lib/experienciasStorage';
import { fetchBlockedDatesFromUrls } from '../lib/icalSync';
import { apiUrl } from '../lib/api';
import { adminApiHeaders } from '../lib/adminApiAuth';
import { fetchPlacePhotos, extractPlaceIdFromUrl } from '../lib/fetchPlacePhotos';
import { processImageAutoAspect } from '../lib/imageProcessing';
import {
  hojeYmdLocal,
  MSG_AVISO_PERIODOS_SOBREPOSTOS,
  periodosStatusTemSobreposicao,
  ymdReferenciaAgendaStatusLocal,
} from '../lib/statusProgramadoUtils';

/** Resultado da busca Google Places para hospedagem */
function toDateInputValue(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function isoFromDateAndTimeLocal(dateStr: string, timeStr: string): string {
  const date = dateStr.trim();
  if (!date) return '';
  const raw = (timeStr?.trim() || '23:59').slice(0, 5);
  const [hh = '0', mm = '0'] = raw.split(':');
  const h = Math.min(23, Math.max(0, parseInt(hh, 10) || 0));
  const m = Math.min(59, Math.max(0, parseInt(mm, 10) || 0));
  const pad = (n: number) => String(n).padStart(2, '0');
  const combined = `${date}T${pad(h)}:${pad(m)}:00`;
  const local = new Date(combined);
  if (Number.isNaN(local.getTime())) return '';
  return local.toISOString();
}

function mergeValidityDateWithSaveTimeNow(dateYmd: string): string {
  const date = dateYmd.trim();
  if (!date) return '';
  const parts = date.split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return '';
  const [y, mo, d] = parts;
  const now = new Date();
  const local = new Date(y, mo - 1, d, now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
  if (Number.isNaN(local.getTime())) return '';
  return local.toISOString();
}

type ModoHospedagemOpcao = 'ativado' | 'principal' | 'naviex' | 'categoria' | 'desativar';

const HOSP_MODO_OPTIONS: { id: ModoHospedagemOpcao; label: string }[] = [
  { id: 'ativado', label: 'Ativado' },
  { id: 'naviex', label: 'Destaque Naviex' },
  { id: 'principal', label: 'Destaque Principal' },
  { id: 'categoria', label: 'Destaque Categoria' },
  { id: 'desativar', label: 'Desativado' },
];

type HospedagemFormStatusSlice = {
  statusPadrao: 'ativado' | 'desativado';
  layoutType: HospedagemLayoutType;
  destaqueSubcategoria: boolean;
  destaqueNaviex: boolean;
};

function getModoHospedagemFromForm(f: HospedagemFormStatusSlice): ModoHospedagemOpcao {
  if (f.statusPadrao === 'desativado') return 'desativar';
  if (f.destaqueNaviex) return 'naviex';
  if (f.layoutType === 'matéria_principal' || f.layoutType === 'slide_principal') return 'principal';
  if (f.destaqueSubcategoria) return 'categoria';
  return 'ativado';
}

function buildUpdateModoHospedagem(m: ModoHospedagemOpcao): Partial<{
  statusPadrao: 'ativado' | 'desativado';
  layoutType: HospedagemLayoutType;
  layoutExpiresAt: string;
  destaqueSubcategoria: boolean;
  destaqueSubcategoriaExpiresAt: string;
  destaqueNaviex: boolean;
  destaqueNaviexExpiresAt: string;
}> {
  switch (m) {
    case 'ativado':
      return {
        statusPadrao: 'ativado',
        layoutType: 'normal',
        layoutExpiresAt: '',
        destaqueSubcategoria: false,
        destaqueSubcategoriaExpiresAt: '',
        destaqueNaviex: false,
        destaqueNaviexExpiresAt: '',
      };
    case 'desativar':
      return {
        statusPadrao: 'desativado',
        layoutType: 'normal',
        layoutExpiresAt: '',
        destaqueSubcategoria: false,
        destaqueSubcategoriaExpiresAt: '',
        destaqueNaviex: false,
        destaqueNaviexExpiresAt: '',
      };
    case 'principal':
      return {
        statusPadrao: 'ativado',
        layoutType: 'matéria_principal',
        destaqueSubcategoria: false,
        destaqueSubcategoriaExpiresAt: '',
        destaqueNaviex: false,
        destaqueNaviexExpiresAt: '',
      };
    case 'categoria':
      return {
        statusPadrao: 'ativado',
        layoutType: 'normal',
        layoutExpiresAt: '',
        destaqueSubcategoria: true,
        destaqueNaviex: false,
        destaqueNaviexExpiresAt: '',
      };
    case 'naviex':
      return {
        statusPadrao: 'ativado',
        layoutType: 'normal',
        layoutExpiresAt: '',
        destaqueSubcategoria: false,
        destaqueSubcategoriaExpiresAt: '',
        destaqueNaviex: true,
      };
  }
}

function statusModoHospButtonClass(m: ModoHospedagemOpcao): string {
  const base =
    'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all min-w-[11rem] max-w-[16rem] justify-between';
  switch (m) {
    case 'desativar':
      return `${base} bg-red-500/15 border-red-500 text-red-800`;
    case 'ativado':
      return `${base} bg-emerald-500/15 border-emerald-500 text-emerald-800`;
    default:
      return `${base} bg-amber-500/12 border-amber-500 text-amber-900`;
  }
}

function statusModoHospDotClass(m: ModoHospedagemOpcao): string {
  switch (m) {
    case 'desativar':
      return 'bg-red-500';
    case 'ativado':
      return 'bg-emerald-500';
    case 'principal':
      return 'bg-amber-500';
    case 'naviex':
      return 'bg-violet-500';
    case 'categoria':
      return 'bg-sky-600';
  }
}

interface ImportPlaceResult {
  googlePlaceId?: string;
  nome: string;
  descricao?: string | null;
  formattedAddress?: string | null;
  lat?: number | null;
  lng?: number | null;
  endereco?: string | null;
  rating?: number | null;
  totalRatings?: number | null;
  source?: 'google_import';
  fotos?: string[];
}

type ViewMode = 'list' | 'form';
type FormTab = 'informacoes' | 'fotos' | 'comodidades' | 'status' | 'calendario' | 'ical' | 'localizacao';

interface FormState {
  nome: string;
  descricao: string;
  tipo: TipoHospedagem;
  cityId: string;
  endereco: string;
  lat?: number;
  lng?: number;
  fotos: string[];
  comodidades: string[];
  quartos: TipoQuarto[];
  checkIn: string;
  checkOut: string;
  politicas: string;
  estrelas?: number;
  bloqueiosManuais: Record<string, { motivo?: string }>;
  icalConfig: {
    icalUrls: ICalUrlItem[];
    lastSyncAt?: string;
    blockedDatesFromIcal: string[];
  };
  statusPadrao: 'ativado' | 'desativado';
  layoutType: HospedagemLayoutType;
  layoutExpiresAt: string;
  destaqueSubcategoria: boolean;
  destaqueSubcategoriaExpiresAt: string;
  destaqueNaviex: boolean;
  destaqueNaviexExpiresAt: string;
  periodosStatus: {
    de: string;
    ate: string;
    tipo: 'ativado' | 'desativado' | 'naviex' | 'principal' | 'categoria';
  }[];
  periodoDe: string;
  periodoAte: string;
  source?: 'manual' | 'google_import';
  googlePlaceId?: string;
  rating?: number;
  totalRatings?: number;
  formattedAddress?: string;
}

const initialForm: FormState = {
  nome: '',
  descricao: '',
  tipo: 'pousada',
  cityId: '',
  endereco: '',
  fotos: [],
  comodidades: [],
  quartos: [],
  checkIn: '14:00',
  checkOut: '11:00',
  politicas: '',
  bloqueiosManuais: {},
  icalConfig: { icalUrls: [], blockedDatesFromIcal: [] },
  statusPadrao: 'ativado',
  layoutType: 'normal',
  layoutExpiresAt: '',
  destaqueSubcategoria: false,
  destaqueSubcategoriaExpiresAt: '',
  destaqueNaviex: false,
  destaqueNaviexExpiresAt: '',
  periodosStatus: [],
  periodoDe: '',
  periodoAte: '',
};

function buildHospedagemStatusAndDestaqueForSave(
  f: FormState,
  hoje: string,
  opts?: { draft?: boolean }
): {
  statusPadrao: 'ativado' | 'desativado';
  periodosStatus:
    | {
        de: string;
        ate: string;
        tipo: 'ativado' | 'desativado' | 'naviex' | 'principal' | 'categoria';
      }[]
    | undefined;
  layoutType: HospedagemLayoutType | undefined;
  layoutExpiresAt: string | undefined;
  destaqueSubcategoria: boolean | undefined;
  destaqueSubcategoriaExpiresAt: string | undefined;
  destaqueNaviex: boolean | undefined;
  destaqueNaviexExpiresAt: string | undefined;
} {
  const principalOn = f.layoutType === 'matéria_principal' || f.layoutType === 'slide_principal';
  const periodosVigentes = (f.periodosStatus ?? []).filter((p) => p.ate >= hoje);
  const mergeDate = opts?.draft
    ? (ymd: string) => isoFromDateAndTimeLocal(ymd, '00:00')
    : mergeValidityDateWithSaveTimeNow;
  return {
    statusPadrao: f.statusPadrao,
    periodosStatus: periodosVigentes.length > 0 ? periodosVigentes : undefined,
    layoutType: principalOn ? f.layoutType : undefined,
    layoutExpiresAt:
      principalOn && toDateInputValue(f.layoutExpiresAt)
        ? mergeDate(toDateInputValue(f.layoutExpiresAt))
        : undefined,
    destaqueSubcategoria: f.destaqueSubcategoria || undefined,
    destaqueSubcategoriaExpiresAt:
      f.destaqueSubcategoria && toDateInputValue(f.destaqueSubcategoriaExpiresAt)
        ? mergeDate(toDateInputValue(f.destaqueSubcategoriaExpiresAt))
        : undefined,
    destaqueNaviex: f.destaqueNaviex || undefined,
    destaqueNaviexExpiresAt:
      f.destaqueNaviex && toDateInputValue(f.destaqueNaviexExpiresAt)
        ? mergeDate(toDateInputValue(f.destaqueNaviexExpiresAt))
        : undefined,
  };
}

export default function HospedagensPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [hospedagens, setHospedagens] = useState<Hospedagem[]>([]);
  const [cidades, setCidades] = useState<ReturnType<typeof getCidades>>([]);
  const [formTab, setFormTab] = useState<FormTab>('informacoes');
  const [calAno, setCalAno] = useState(() => new Date().getFullYear());
  const [calMes, setCalMes] = useState(() => new Date().getMonth());
  const [icalSyncLoading, setIcalSyncLoading] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importCityId, setImportCityId] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importFotosLoading, setImportFotosLoading] = useState(false);
  const [importResults, setImportResults] = useState<ImportPlaceResult[]>([]);
  const [novaFotoUrl, setNovaFotoUrl] = useState('');
  const isFirstFormRenderRef = useRef(true);
  const persistHospDraftRef = useRef<(opts?: { keepOnForm?: boolean }) => boolean>(() => false);
  const saveHospPublishedRef = useRef<() => Promise<boolean>>(async () => false);
  const autoDraftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statusComboRef = useRef<HTMLDivElement>(null);
  const [statusComboContext, setStatusComboContext] = useState<'header' | null>(null);
  const {
    isDirty,
    setDirty,
    setIsPublishedLeave,
    setFlushDraftHandler,
    setSavePublishedHandler,
  } = useAdminUnsaved();
  const { showAlert, showConfirm } = useAdminDialog();

  const [form, setForm] = useState<FormState>({ ...initialForm });

  const refresh = useCallback(() => {
    setHospedagens(getHospedagens());
    setCidades(getCidades());
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useRefetchWhenCatalogChanges(() => refresh());

  const hojePeriodo = hojeYmdLocal();
  const modoHospedagemAtual = useMemo(() => getModoHospedagemFromForm(form), [form]);

  useEffect(() => {
    if (!statusComboContext) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const inHeader = statusComboRef.current?.contains(target);
      if (!inHeader) setStatusComboContext(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [statusComboContext]);

  useOnIcalSynced(
    useCallback(() => {
      refresh();
      if (editingId && !isDirty) {
        const h = getHospedagemById(editingId);
        if (h?.icalConfig) {
          setForm((f) => ({
            ...f,
            icalConfig: {
              ...f.icalConfig,
              icalUrls: h.icalConfig!.icalUrls ?? [],
              blockedDatesFromIcal: h.icalConfig!.blockedDatesFromIcal ?? [],
              lastSyncAt: h.icalConfig!.lastSyncAt,
            },
          }));
        }
      }
    }, [refresh, editingId, isDirty])
  );

  const openNew = () => {
    setForm({ ...initialForm, cityId: cidades[0]?.id ?? '' });
    setEditingId(null);
    setFormTab('informacoes');
    setDirty(false);
    isFirstFormRenderRef.current = true;
    setViewMode('form');
  };

  const openEdit = (id: string) => {
    const h = getHospedagemById(id);
    if (!h) return;
    const ical = h.icalConfig;
    const icalUrls = ical?.icalUrls ?? [];
    setForm({
      nome: h.nome,
      descricao: h.descricao ?? '',
      tipo: h.tipo,
      cityId: h.cityId ?? '',
      endereco: h.endereco ?? '',
      lat: h.lat,
      lng: h.lng,
      fotos: h.fotos ?? [],
      comodidades: h.comodidades ?? [],
      quartos: h.quartos ?? [],
      checkIn: h.checkIn ?? '14:00',
      checkOut: h.checkOut ?? '11:00',
      politicas: h.politicas ?? '',
      estrelas: h.estrelas,
      bloqueiosManuais: h.bloqueiosManuais ?? {},
      icalConfig: {
        icalUrls,
        lastSyncAt: ical?.lastSyncAt,
        blockedDatesFromIcal: ical?.blockedDatesFromIcal ?? [],
      },
      statusPadrao: h.statusPadrao,
      layoutType: (h.layoutType ?? 'normal') as HospedagemLayoutType,
      layoutExpiresAt: h.layoutExpiresAt ?? '',
      destaqueSubcategoria: h.destaqueSubcategoria ?? false,
      destaqueSubcategoriaExpiresAt: h.destaqueSubcategoriaExpiresAt ?? '',
      destaqueNaviex: h.destaqueNaviex ?? false,
      destaqueNaviexExpiresAt: h.destaqueNaviexExpiresAt ?? '',
      periodosStatus: (h.periodosStatus ?? []).filter((p) => p.ate >= hojeYmdLocal()),
      periodoDe: '',
      periodoAte: '',
      source: h.source,
      googlePlaceId: h.googlePlaceId,
      rating: h.rating,
      totalRatings: h.totalRatings,
      formattedAddress: h.formattedAddress,
    });
    setEditingId(id);
    setFormTab('informacoes');
    setDirty(false);
    isFirstFormRenderRef.current = true;
    setViewMode('form');
  };

  useEffect(() => {
    if (viewMode !== 'form') {
      isFirstFormRenderRef.current = true;
      return;
    }
    if (isFirstFormRenderRef.current) {
      isFirstFormRenderRef.current = false;
      return;
    }
    setDirty(true);
  }, [form, viewMode, setDirty]);

  const fetchImportPlaces = async () => {
    const city = cidades.find((c) => c.id === importCityId);
    if (!city?.cidade) {
      await showAlert('Selecione a cidade.');
      return;
    }
    setImportLoading(true);
    setImportResults([]);
    try {
      const res = await fetch(apiUrl('/api/admin/import-google-places'), {
        method: 'POST',
        headers: adminApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ city: city.cidade, type: 'lodging' }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string; details?: string };
        const msg = err.error ?? `Erro ${res.status}`;
        throw new Error(err.details ? `${msg}: ${err.details}` : msg);
      }
      const data = (await res.json()) as { places?: ImportPlaceResult[] };
      setImportResults(data.places ?? []);
      if ((data.places ?? []).length === 0) await showAlert('Nenhum resultado encontrado.');
    } catch (e) {
      await showAlert(e instanceof Error ? e.message : 'Erro ao buscar do Google Places.');
    } finally {
      setImportLoading(false);
    }
  };

  const applyImportToForm = (place: ImportPlaceResult) => {
    const cityId = importCityId || (cidades[0]?.id ?? '');
    setForm((f) => ({
      ...f,
      nome: place.nome ?? f.nome,
      descricao: place.descricao ?? f.descricao,
      cityId,
      endereco: place.endereco ?? place.formattedAddress ?? f.endereco,
      lat: place.lat ?? f.lat,
      lng: place.lng ?? f.lng,
      fotos: (place.fotos?.length ? place.fotos : f.fotos) ?? [],
      formattedAddress: place.formattedAddress ?? f.formattedAddress,
      source: 'google_import',
      googlePlaceId: place.googlePlaceId ?? f.googlePlaceId,
      rating: place.rating ?? f.rating,
      totalRatings: place.totalRatings ?? f.totalRatings,
    }));
    setImportModalOpen(false);
    setFormTab('informacoes');
    setViewMode('form');
    setDirty(true);
  };

  const handleImportarFotos = async () => {
    const placeId = form.googlePlaceId ?? extractPlaceIdFromUrl(form.endereco ?? '');
    if (!placeId) {
      await showAlert('Importe do Google Places primeiro ou cole o link do Google Maps no campo Endereço.');
      return;
    }
    setImportFotosLoading(true);
    try {
      const { fotos } = await fetchPlacePhotos(placeId);
      if (!fotos.length) {
        await showAlert('Nenhuma foto encontrada para este lugar.');
        return;
      }
      const processed: string[] = [];
      for (let i = 0; i < fotos.length; i++) {
        try {
          processed.push(await processImageAutoAspect(fotos[i]));
        } catch {
          processed.push(fotos[i]);
        }
      }
      setForm((f) => ({ ...f, fotos: [...f.fotos, ...processed] }));
      setDirty(true);
      await showAlert(`${processed.length} foto(s) importada(s) e ajustadas para 9:16 ou 16:9.`);
    } catch (e) {
      await showAlert(e instanceof Error ? e.message : 'Erro ao importar fotos.');
    } finally {
      setImportFotosLoading(false);
    }
  };

  const handleIcalSync = async () => {
    const urls = form.icalConfig.icalUrls
      .map((u) => u.url?.trim())
      .filter((u): u is string => !!u && u.startsWith('http'));
    if (urls.length === 0) {
      await showAlert('Informe ao menos uma URL iCal (Booking ou Airbnb).');
      return;
    }
    setIcalSyncLoading(true);
    try {
      const blocked = await fetchBlockedDatesFromUrls(urls);
      setForm((f) => ({
        ...f,
        icalConfig: {
          ...f.icalConfig,
          blockedDatesFromIcal: blocked,
          lastSyncAt: new Date().toISOString(),
        },
      }));
      setDirty(true);
      await showAlert(`Sincronizado! ${blocked.length} data(s) bloqueada(s) encontrada(s).`);
    } catch (e) {
      await showAlert(
        e instanceof Error
          ? e.message
          : 'Erro ao buscar calendários. Verifique as URLs. Se houver erro de CORS, use um proxy no backend.'
      );
    } finally {
      setIcalSyncLoading(false);
    }
  };

  const adicionarPeriodoStatus = async (
    tipo: 'ativado' | 'desativado' | 'naviex' | 'principal' | 'categoria'
  ) => {
    let de = form.periodoDe?.trim() || '';
    let ate = form.periodoAte?.trim() || '';
    if (!de) return;
    if (!ate) ate = de;
    if (de > ate) [de, ate] = [ate, de];
    const umSoDia = de === ate;
    if (umSoDia) {
      if (tipo === 'ativado' || tipo === 'desativado') {
        setForm((f) => ({ ...f, statusPadrao: tipo, periodoDe: '', periodoAte: '' }));
      } else {
        const modo = tipo === 'naviex' ? 'naviex' : tipo === 'principal' ? 'principal' : 'categoria';
        setForm((f) => ({ ...f, ...buildUpdateModoHospedagem(modo), periodoDe: '', periodoAte: '' }));
      }
      setDirty(true);
      return;
    }
    setForm((f) => ({
      ...f,
      periodosStatus: [...f.periodosStatus, { de, ate, tipo }].sort((a, b) => a.de.localeCompare(b.de)),
      periodoDe: '',
      periodoAte: '',
    }));
    setDirty(true);
  };

  const removerPeriodoStatus = (index: number) => {
    setForm((f) => ({ ...f, periodosStatus: f.periodosStatus.filter((_, i) => i !== index) }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!form.nome.trim()) {
      await showAlert('Nome é obrigatório.');
      return;
    }
    if (!form.descricao.trim()) {
      await showAlert('Descrição é obrigatória.');
      return;
    }
    if (!form.cityId) {
      await showAlert('Cidade é obrigatória.');
      return;
    }

    if (periodosStatusTemSobreposicao(form.periodosStatus ?? [])) {
      const ok = await showConfirm(MSG_AVISO_PERIODOS_SOBREPOSTOS, {
        title: 'Períodos sobrepostos',
        confirmText: 'Continuar',
        cancelText: 'Cancelar',
      });
      if (!ok) return;
    }

    const hoje = hojeYmdLocal();
    const statusDest = buildHospedagemStatusAndDestaqueForSave(form, hoje);

    const payload: Omit<Hospedagem, 'id' | 'createdAt' | 'updatedAt'> = {
      nome: form.nome.trim(),
      descricao: form.descricao.trim(),
      tipo: form.tipo,
      cityId: form.cityId,
      endereco: form.endereco.trim() || undefined,
      lat: form.lat,
      lng: form.lng,
      fotos: form.fotos,
      comodidades: form.comodidades,
      quartos: form.quartos.filter((q) => (q.tipo ?? '').trim() && q.quantidade >= 1 && q.capacidade >= 1),
      checkIn: form.checkIn,
      checkOut: form.checkOut,
      politicas: form.politicas.trim() || undefined,
      estrelas: form.estrelas,
      bloqueiosManuais: form.bloqueiosManuais,
      icalConfig:
        form.icalConfig.icalUrls.some((u) => u.url?.trim())
          ? {
              icalUrls: form.icalConfig.icalUrls.filter((u) => u.url?.trim()),
              lastSyncAt: form.icalConfig.lastSyncAt,
              blockedDatesFromIcal: form.icalConfig.blockedDatesFromIcal,
            }
          : undefined,
      ...statusDest,
      source: form.source,
      googlePlaceId: form.googlePlaceId,
      rating: form.rating,
      totalRatings: form.totalRatings,
      formattedAddress: form.formattedAddress,
      publicacaoStatus: 'publicado',
    };

    if (editingId) {
      const curr = getHospedagemById(editingId);
      if (!curr) {
        await showAlert('Hospedagem não encontrada.');
        return;
      }
      saveHospedagem({ ...curr, ...payload });
    } else {
      addHospedagem(payload);
    }
    setDirty(false);
    refresh();
    setViewMode('list');
    setEditingId(null);
  };

  const persistHospedagemDraftSilent = (opts?: { keepOnForm?: boolean }): boolean => {
    const cityId = form.cityId || cidades[0]?.id || '';
    if (!cityId) return false;
    const hoje = hojeYmdLocal();
    const statusDest = buildHospedagemStatusAndDestaqueForSave(form, hoje, { draft: true });
    const payload: Omit<Hospedagem, 'id' | 'createdAt' | 'updatedAt'> = {
      nome: form.nome.trim() || 'Rascunho',
      descricao: form.descricao.trim() || '—',
      tipo: form.tipo,
      cityId,
      endereco: form.endereco.trim() || undefined,
      lat: form.lat,
      lng: form.lng,
      fotos: form.fotos,
      comodidades: form.comodidades,
      quartos: form.quartos.filter((q) => (q.tipo ?? '').trim() && q.quantidade >= 1 && q.capacidade >= 1),
      checkIn: form.checkIn,
      checkOut: form.checkOut,
      politicas: form.politicas.trim() || undefined,
      estrelas: form.estrelas,
      bloqueiosManuais: form.bloqueiosManuais,
      icalConfig:
        form.icalConfig.icalUrls.some((u) => u.url?.trim())
          ? {
              icalUrls: form.icalConfig.icalUrls.filter((u) => u.url?.trim()),
              lastSyncAt: form.icalConfig.lastSyncAt,
              blockedDatesFromIcal: form.icalConfig.blockedDatesFromIcal,
            }
          : undefined,
      ...statusDest,
      source: form.source,
      googlePlaceId: form.googlePlaceId,
      rating: form.rating,
      totalRatings: form.totalRatings,
      formattedAddress: form.formattedAddress,
      publicacaoStatus: 'rascunho',
    };
    try {
      if (editingId) {
        const curr = getHospedagemById(editingId);
        if (!curr) return false;
        saveHospedagem({ ...curr, ...payload });
      } else {
        const added = addHospedagem(payload);
        if (opts?.keepOnForm) setEditingId(added.id);
      }
    } catch {
      return false;
    }
    setDirty(false);
    refresh();
    if (!opts?.keepOnForm) {
      setViewMode('list');
      setEditingId(null);
    }
    return true;
  };

  const savePublishedHospedagemForLeave = async (): Promise<boolean> => {
    if (!form.nome.trim()) {
      await showAlert('Nome é obrigatório.');
      return false;
    }
    if (!form.descricao.trim()) {
      await showAlert('Descrição é obrigatória.');
      return false;
    }
    if (!form.cityId) {
      await showAlert('Cidade é obrigatória.');
      return false;
    }
    if (periodosStatusTemSobreposicao(form.periodosStatus ?? [])) {
      const ok = await showConfirm(MSG_AVISO_PERIODOS_SOBREPOSTOS, {
        title: 'Períodos sobrepostos',
        confirmText: 'Continuar',
        cancelText: 'Cancelar',
      });
      if (!ok) return false;
    }
    const hojePub = hojeYmdLocal();
    const statusDestPub = buildHospedagemStatusAndDestaqueForSave(form, hojePub);
    const payload: Omit<Hospedagem, 'id' | 'createdAt' | 'updatedAt'> = {
      nome: form.nome.trim(),
      descricao: form.descricao.trim(),
      tipo: form.tipo,
      cityId: form.cityId,
      endereco: form.endereco.trim() || undefined,
      lat: form.lat,
      lng: form.lng,
      fotos: form.fotos,
      comodidades: form.comodidades,
      quartos: form.quartos.filter((q) => (q.tipo ?? '').trim() && q.quantidade >= 1 && q.capacidade >= 1),
      checkIn: form.checkIn,
      checkOut: form.checkOut,
      politicas: form.politicas.trim() || undefined,
      estrelas: form.estrelas,
      bloqueiosManuais: form.bloqueiosManuais,
      icalConfig:
        form.icalConfig.icalUrls.some((u) => u.url?.trim())
          ? {
              icalUrls: form.icalConfig.icalUrls.filter((u) => u.url?.trim()),
              lastSyncAt: form.icalConfig.lastSyncAt,
              blockedDatesFromIcal: form.icalConfig.blockedDatesFromIcal,
            }
          : undefined,
      ...statusDestPub,
      source: form.source,
      googlePlaceId: form.googlePlaceId,
      rating: form.rating,
      totalRatings: form.totalRatings,
      formattedAddress: form.formattedAddress,
      publicacaoStatus: 'publicado',
    };
    if (editingId) {
      const curr = getHospedagemById(editingId);
      if (!curr) {
        await showAlert('Hospedagem não encontrada.');
        return false;
      }
      saveHospedagem({ ...curr, ...payload });
    } else {
      addHospedagem(payload);
    }
    setDirty(false);
    refresh();
    setViewMode('list');
    setEditingId(null);
    return true;
  };

  persistHospDraftRef.current = persistHospedagemDraftSilent;
  saveHospPublishedRef.current = savePublishedHospedagemForLeave;

  useEffect(() => {
    if (viewMode !== 'form') {
      setIsPublishedLeave(false);
      setFlushDraftHandler(null);
      setSavePublishedHandler(null);
      return;
    }
    const storedPub = editingId ? (getHospedagemById(editingId)?.publicacaoStatus ?? 'publicado') : 'rascunho';
    const isRascunho = storedPub === 'rascunho';
    setIsPublishedLeave(Boolean(isDirty && storedPub === 'publicado'));
    setFlushDraftHandler(
      isRascunho && isDirty ? async () => persistHospDraftRef.current({ keepOnForm: true }) : null
    );
    setSavePublishedHandler(
      storedPub === 'publicado' && isDirty ? async () => saveHospPublishedRef.current() : null
    );
    return () => {
      setFlushDraftHandler(null);
      setSavePublishedHandler(null);
    };
  }, [viewMode, editingId, isDirty, setIsPublishedLeave, setFlushDraftHandler, setSavePublishedHandler]);

  useEffect(() => {
    if (viewMode !== 'form') {
      if (autoDraftTimerRef.current) {
        clearTimeout(autoDraftTimerRef.current);
        autoDraftTimerRef.current = null;
      }
      return;
    }
    const storedPub = editingId ? (getHospedagemById(editingId)?.publicacaoStatus ?? 'publicado') : 'rascunho';
    if (storedPub !== 'rascunho') {
      if (autoDraftTimerRef.current) {
        clearTimeout(autoDraftTimerRef.current);
        autoDraftTimerRef.current = null;
      }
      return;
    }
    if (isFirstFormRenderRef.current) return;
    if (!isDirty) return;
    if (autoDraftTimerRef.current) clearTimeout(autoDraftTimerRef.current);
    autoDraftTimerRef.current = setTimeout(() => {
      autoDraftTimerRef.current = null;
      void persistHospDraftRef.current({ keepOnForm: true });
    }, 1200);
    return () => {
      if (autoDraftTimerRef.current) {
        clearTimeout(autoDraftTimerRef.current);
        autoDraftTimerRef.current = null;
      }
    };
  }, [form, viewMode, editingId, isDirty]);

  const handleCancel = () => {
    setDirty(false);
    setViewMode('list');
    setEditingId(null);
  };

  const handleDelete = async (h: Hospedagem) => {
    const ok = await showConfirm(`Excluir "${h.nome}"?`, {
      title: 'Excluir hospedagem',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
    });
    if (!ok) return;
    removeHospedagem(h.id);
    refresh();
  };

  const toggleBloqueioManual = (dataStr: string) => {
    setForm((f) => {
      const next = { ...f.bloqueiosManuais };
      if (next[dataStr]) delete next[dataStr];
      else next[dataStr] = {};
      return { ...f, bloqueiosManuais: next };
    });
    setDirty(true);
  };

  const blockedDates = new Set([
    ...Object.keys(form.bloqueiosManuais),
    ...form.icalConfig.blockedDatesFromIcal,
  ]);

  const getDaysInMonth = (year: number, month: number) => {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    return { days: last.getDate(), startPad: first.getDay() };
  };

  const tipoLabel = (t: TipoHospedagem) => TIPOS_HOSPEDAGEM.find((x) => x.value === t)?.label ?? t;

  const quartosResumo = (h: Hospedagem) => {
    const qs = h.quartos ?? [];
    if (qs.length === 0) return '—';
    const totalQt = qs.reduce((s, q) => s + q.quantidade, 0);
    const totalCap = qs.reduce((s, q) => s + q.quantidade * q.capacidade, 0);
    return `${totalQt} quarto(s), ${totalCap} pessoas`;
  };

  // ========== LIST VIEW ==========
  if (viewMode === 'list') {
    return (
      <AdminLayout>
        <div className="w-full min-w-0">
          <div className="sticky top-0 z-20 px-4 sm:px-6 py-6 border-b border-gray-200 bg-white shadow-sm">
            <nav className="text-sm text-gray-500 mb-1">
              <Link to="/dashboard" className="hover:text-gray-700">Home</Link>
              {' > '}
              <Link to="/hospedagens" className="hover:text-gray-700">Hospedagem</Link>
            </nav>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Hospedagens</h1>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setImportModalOpen(true)}
                  className="px-4 py-2 min-h-[44px] border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                >
                  Importar do Google
                </button>
                <button
                  type="button"
                  onClick={openNew}
                  className="px-4 py-2 min-h-[44px] bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600"
                >
                  Nova Hospedagem
                </button>
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-6 py-6">
            <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
              {hospedagens.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <p>Nenhuma hospedagem cadastrada.</p>
                  <button type="button" onClick={openNew} className="mt-4 text-orange-600 font-medium hover:underline">
                    Criar a primeira
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-sm">
                    <thead>
                      <tr className="bg-orange-500 text-white">
                        <th className="text-left py-3 px-4 font-semibold">Nome</th>
                        <th className="text-left py-3 px-4 font-semibold">Tipo</th>
                        <th className="text-left py-3 px-4 font-semibold">Quartos</th>
                        <th className="text-left py-3 px-4 font-semibold">Cidade</th>
                        <th className="text-left py-3 px-4 font-semibold">Status</th>
                        <th className="text-right py-3 px-4 font-semibold">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hospedagens.map((h, idx) => (
                        <tr key={h.id} className={`${['bg-white', 'bg-gray-50', 'bg-orange-50'][idx % 3]} border-b border-gray-100 hover:opacity-90 transition-opacity`}>
                          <td className="py-3 px-4">{h.nome}</td>
                          <td className="py-3 px-4 text-gray-600">{tipoLabel(h.tipo)}</td>
                          <td className="py-3 px-4 text-gray-600">{quartosResumo(h)}</td>
                          <td className="py-3 px-4 text-gray-600">
                            {h.cityId ? cidades.find((c) => c.id === h.cityId)?.cidade ?? '—' : '—'}
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                isHospedagemAtiva(h) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {isHospedagemAtiva(h) ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex flex-wrap items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => openEdit(h.id)}
                                className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(h)}
                                className="px-3 py-1.5 text-xs border border-red-200 text-red-600 rounded-lg hover:bg-red-50"
                              >
                                Excluir
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Import Modal */}
          {importModalOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-[320] flex items-center justify-center p-4"
              onClick={() => setImportModalOpen(false)}
            >
              <div
                className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Importar Hospedagens do Google Places</h3>
                  <p className="text-sm text-gray-600">Busque na cidade e preencha o formulário.</p>
                </div>
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                    <select
                      value={importCityId}
                      onChange={(e) => setImportCityId(e.target.value)}
                      className="w-full py-2 px-3 rounded-lg border border-gray-300"
                    >
                      <option value="">Selecione a cidade</option>
                      {cidades.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.cidade}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => void fetchImportPlaces()}
                    disabled={importLoading || !importCityId}
                    className="w-full py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {importLoading ? 'Buscando...' : 'Buscar no Google Places'}
                  </button>
                  {importResults.length > 0 && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <p className="text-sm font-medium text-gray-700 p-3 bg-gray-50 border-b">
                        Resultados ({importResults.length}) — clique para preencher
                      </p>
                      <ul className="max-h-[280px] overflow-y-auto divide-y divide-gray-100">
                        {importResults.map((place, i) => (
                          <li
                            key={place.googlePlaceId ?? i}
                            className="flex items-center justify-between p-3 hover:bg-orange-50 cursor-pointer"
                            onClick={() => applyImportToForm(place)}
                          >
                            <div>
                              <p className="font-medium text-gray-900">{place.nome}</p>
                              {place.formattedAddress && (
                                <p className="text-xs text-gray-500 truncate max-w-md">{place.formattedAddress}</p>
                              )}
                              {place.rating != null && (
                                <p className="text-xs text-amber-600 mt-0.5">
                                  ★ {place.rating.toFixed(1)}
                                  {place.totalRatings != null ? ` (${place.totalRatings} avaliações)` : ''}
                                </p>
                              )}
                            </div>
                            <span className="text-orange-600 text-sm font-medium">Usar</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="p-4 border-t border-gray-200 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setImportModalOpen(false)}
                    className="px-4 py-2.5 rounded-xl font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </AdminLayout>
    );
  }

  // ========== FORM VIEW ==========
  const tabs: { id: FormTab; label: string }[] = [
    { id: 'informacoes', label: 'Informações' },
    { id: 'fotos', label: 'Fotos' },
    { id: 'comodidades', label: 'Comodidades' },
    { id: 'status', label: 'Status Programado' },
    { id: 'calendario', label: 'Calendário' },
    { id: 'ical', label: 'Sync Booking/Airbnb' },
    { id: 'localizacao', label: 'Localização' },
  ];

  const { days, startPad } = getDaysInMonth(calAno, calMes);
  const mesLabel = new Date(calAno, calMes, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  return (
    <AdminLayout>
      <div className="w-full min-w-0">
        <div className="sticky top-0 z-20 px-4 sm:px-6 py-6 border-b border-gray-200 bg-white shadow-sm">
          <nav className="text-sm text-gray-500 mb-1">
            <Link to="/dashboard" className="hover:text-gray-700">Home</Link>
            {' > '}
            <Link to="/hospedagens" className="hover:text-gray-700">Hospedagem</Link>
            {' > '}
            <span className="text-gray-800">{form.nome.trim() || (editingId ? 'Editar' : 'Nova')}</span>
          </nav>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3 flex-wrap min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                {form.nome.trim() || (editingId ? 'Editar hospedagem' : 'Nova hospedagem')}
              </h1>
              <div className="relative shrink-0" ref={statusComboRef}>
                <button
                  type="button"
                  onClick={() => setStatusComboContext((c) => (c === 'header' ? null : 'header'))}
                  className={statusModoHospButtonClass(modoHospedagemAtual)}
                  aria-haspopup="listbox"
                  aria-expanded={statusComboContext === 'header'}
                >
                  <span className="inline-flex items-center gap-2 min-w-0">
                    <span className={`relative flex h-2 w-2 shrink-0 rounded-full ${statusModoHospDotClass(modoHospedagemAtual)}`} />
                    <span className="truncate text-left">
                      {HOSP_MODO_OPTIONS.find((o) => o.id === modoHospedagemAtual)?.label ?? 'Status'}
                    </span>
                  </span>
                  <svg
                    className={`w-4 h-4 shrink-0 transition-transform ${statusComboContext === 'header' ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {statusComboContext === 'header' && (
                  <div
                    className="absolute top-full left-0 mt-1 py-1 rounded-lg border-2 border-gray-200 bg-white shadow-lg z-10 min-w-[15rem] max-w-[min(100vw-2rem,20rem)]"
                    role="listbox"
                  >
                    {HOSP_MODO_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        role="option"
                        aria-selected={opt.id === modoHospedagemAtual}
                        onClick={() => {
                          setForm((f) => ({ ...f, ...buildUpdateModoHospedagem(opt.id) }));
                          setStatusComboContext(null);
                          setDirty(true);
                        }}
                        className={`w-full inline-flex items-center gap-2 px-4 py-2.5 text-left text-sm font-medium ${
                          opt.id === modoHospedagemAtual ? 'bg-orange-50/80' : 'hover:bg-gray-50'
                        }`}
                      >
                        <span className={`rounded-full h-2 w-2 shrink-0 ${statusModoHospDotClass(opt.id)}`} />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 min-h-[44px] border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="px-4 py-2 min-h-[44px] bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 sm:px-6 py-6">
          {/* Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setFormTab(t.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap ${
                  formTab === t.id ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Tab: Informações */}
          {formTab === 'informacoes' && (
            <div className="space-y-4 max-w-2xl">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                <input
                  type="text"
                  value={form.nome}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, nome: e.target.value }));
                    setDirty(true);
                  }}
                  className="w-full py-2 px-3 rounded-lg border border-gray-300"
                  placeholder="Nome da hospedagem"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição *</label>
                <textarea
                  value={form.descricao}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, descricao: e.target.value }));
                    setDirty(true);
                  }}
                  rows={4}
                  className="w-full py-2 px-3 rounded-lg border border-gray-300"
                  placeholder="Descrição detalhada"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                  <select
                    value={form.tipo}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, tipo: e.target.value as TipoHospedagem }));
                      setDirty(true);
                    }}
                    className="w-full py-2 px-3 rounded-lg border border-gray-300"
                  >
                    {TIPOS_HOSPEDAGEM.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cidade *</label>
                  <select
                    value={form.cityId}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, cityId: e.target.value }));
                      setDirty(true);
                    }}
                    className="w-full py-2 px-3 rounded-lg border border-gray-300"
                  >
                    <option value="">Selecione</option>
                    {cidades.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.cidade}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estrelas</label>
                <select
                  value={form.estrelas ?? ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((f) => ({ ...f, estrelas: v ? parseInt(v, 10) : undefined }));
                    setDirty(true);
                  }}
                  className="w-full max-w-[120px] py-2 px-3 rounded-lg border border-gray-300"
                >
                  <option value="">—</option>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>
                      {n} ★
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipos de quarto</label>
                <p className="text-xs text-gray-500 mb-2">
                  Cadastre cada tipo (ex: Duplo, Triplo, Suíte) com quantidade e capacidade.
                </p>
                <div className="space-y-2">
                  {form.quartos.map((q, i) => (
                    <div key={i} className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <input
                        type="text"
                        value={q.tipo}
                        onChange={(e) => {
                          setForm((f) => ({
                            ...f,
                            quartos: f.quartos.map((qu, j) => (j === i ? { ...qu, tipo: e.target.value } : qu)),
                          }));
                          setDirty(true);
                        }}
                        placeholder="Tipo (ex: Duplo)"
                        className="flex-1 min-w-[100px] py-2 px-3 rounded border border-gray-300"
                      />
                      <input
                        type="number"
                        min={1}
                        value={q.quantidade}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          if (!Number.isNaN(v) && v >= 1)
                            setForm((f) => ({
                              ...f,
                              quartos: f.quartos.map((qu, j) => (j === i ? { ...qu, quantidade: v } : qu)),
                            }));
                          setDirty(true);
                        }}
                        placeholder="Qtd"
                        className="w-16 py-2 px-2 rounded border border-gray-300 text-center"
                      />
                      <span className="text-gray-500 text-sm">quarto(s)</span>
                      <input
                        type="number"
                        min={1}
                        value={q.capacidade}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          if (!Number.isNaN(v) && v >= 1)
                            setForm((f) => ({
                              ...f,
                              quartos: f.quartos.map((qu, j) => (j === i ? { ...qu, capacidade: v } : qu)),
                            }));
                          setDirty(true);
                        }}
                        placeholder="Cap."
                        className="w-16 py-2 px-2 rounded border border-gray-300 text-center"
                      />
                      <span className="text-gray-500 text-sm">pessoas cada</span>
                      <button
                        type="button"
                        onClick={() => {
                          setForm((f) => ({ ...f, quartos: f.quartos.filter((_, j) => j !== i) }));
                          setDirty(true);
                        }}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Remover"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setForm((f) => ({
                      ...f,
                      quartos: [...f.quartos, { tipo: '', quantidade: 1, capacidade: 2 }],
                    }));
                    setDirty(true);
                  }}
                  className="mt-2 px-3 py-2 text-sm border border-orange-300 text-orange-600 rounded-lg hover:bg-orange-50"
                >
                  + Adicionar tipo de quarto
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-in</label>
                  <input
                    type="time"
                    value={form.checkIn}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, checkIn: e.target.value }));
                      setDirty(true);
                    }}
                    className="w-full py-2 px-3 rounded-lg border border-gray-300"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Check-out</label>
                  <input
                    type="time"
                    value={form.checkOut}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, checkOut: e.target.value }));
                      setDirty(true);
                    }}
                    className="w-full py-2 px-3 rounded-lg border border-gray-300"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Políticas (cancelamento, etc.)</label>
                <textarea
                  value={form.politicas}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, politicas: e.target.value }));
                    setDirty(true);
                  }}
                  rows={2}
                  className="w-full py-2 px-3 rounded-lg border border-gray-300"
                  placeholder="Ex: Cancelamento gratuito até 24h antes"
                />
              </div>
            </div>
          )}

          {/* Tab: Fotos */}
          {formTab === 'fotos' && (
            <div className="space-y-4 max-w-2xl">
              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  type="button"
                  onClick={handleImportarFotos}
                  disabled={importFotosLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50"
                >
                  {importFotosLoading ? 'Importando...' : 'Importar fotos do Google'}
                </button>
                <span className="text-xs text-gray-500 self-center">Importe do Google Places primeiro ou use link do Maps no Endereço.</span>
              </div>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={novaFotoUrl}
                  onChange={(e) => setNovaFotoUrl(e.target.value)}
                  placeholder="URL da foto"
                  className="flex-1 py-2 px-3 rounded-lg border border-gray-300"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (novaFotoUrl.trim()) {
                      setForm((f) => ({ ...f, fotos: [...f.fotos, novaFotoUrl.trim()] }));
                      setNovaFotoUrl('');
                      setDirty(true);
                    }
                  }}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600"
                >
                  Adicionar
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {form.fotos.map((url, i) => (
                  <div key={i} className="relative group">
                    <img
                      src={url}
                      alt={`NaviExperience - ${form.nome || 'Hospedagem'} - Foto ${i + 1}`}
                      title={`NaviExperience - ${form.nome || 'Hospedagem'} - Foto ${i + 1}`}
                      className="w-full aspect-[4/3] object-cover rounded-lg border border-gray-200"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect fill="%23ddd" width="100" height="100"/><text x="50" y="50" fill="%23999" text-anchor="middle" dy=".3em" font-size="12">Erro</text></svg>';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setForm((f) => ({ ...f, fotos: f.fotos.filter((_, j) => j !== i) }));
                        setDirty(true);
                      }}
                      className="absolute top-1 right-1 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tab: Comodidades */}
          {formTab === 'comodidades' && (
            <div className="space-y-4 max-w-2xl">
              <p className="text-sm text-gray-600">Marque as comodidades disponíveis:</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {COMODIDADES_LIST.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={form.comodidades.includes(c.id)}
                      onChange={(e) => {
                        setForm((f) => ({
                          ...f,
                          comodidades: e.target.checked
                            ? [...f.comodidades, c.id]
                            : f.comodidades.filter((x) => x !== c.id),
                        }));
                        setDirty(true);
                      }}
                      className="rounded border-gray-300 text-orange-500"
                    />
                    <span className="text-sm">{c.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {formTab === 'status' && (
            <section className="bg-gray-50 rounded-xl p-6 border border-gray-200 w-full max-w-4xl">
                <h2 className="text-lg font-semibold text-gray-800 shrink-0 tracking-wide mb-4">Status Programado</h2>

              <div className="space-y-3">
                <div className="text-xs font-semibold tracking-wide text-gray-600">Status Programado</div>
                <label className="block text-sm font-medium text-gray-700">Agendamento de status (visibilidade e destaques)</label>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
                  <div className="min-w-0 max-w-md">
                    <span className="block text-sm font-medium text-gray-700 mb-2">Período (Data)</span>
                    <PeriodoDataCalendar
                      minDate={hojePeriodo}
                      periodoDe={form.periodoDe}
                      periodoAte={form.periodoAte}
                      periodosAgendaVisual={form.periodosStatus ?? []}
                      diaReferenciaAgendaVisual={ymdReferenciaAgendaStatusLocal()}
                      onChange={(de, ate) => setForm((f) => ({ ...f, periodoDe: de, periodoAte: ate }))}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0 lg:pt-7">
                    <button
                      type="button"
                      onClick={() => void adicionarPeriodoStatus('ativado')}
                      className="px-3 py-2 rounded-lg text-sm font-medium border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                    >
                      Ativar
                    </button>
                    <button
                      type="button"
                      onClick={() => void adicionarPeriodoStatus('naviex')}
                      className="px-3 py-2 rounded-lg text-sm font-medium border border-violet-300 text-violet-800 bg-violet-50 hover:bg-violet-100 transition-colors"
                    >
                      Destaque Naviex
                    </button>
                    <button
                      type="button"
                      onClick={() => void adicionarPeriodoStatus('principal')}
                      className="px-3 py-2 rounded-lg text-sm font-medium border border-amber-400 text-amber-900 bg-amber-50 hover:bg-amber-100 transition-colors"
                    >
                      Destaque Principal
                    </button>
                    <button
                      type="button"
                      onClick={() => void adicionarPeriodoStatus('categoria')}
                      className="px-3 py-2 rounded-lg text-sm font-medium border border-orange-300 text-orange-900 bg-orange-50 hover:bg-orange-100 transition-colors"
                    >
                      Destaque Categoria
                    </button>
                    <button
                      type="button"
                      onClick={() => void adicionarPeriodoStatus('desativado')}
                      className="px-3 py-2 rounded-lg text-sm font-medium border border-red-300 text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
                    >
                      Desativado
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  <strong>Um só dia:</strong> duplo clique no dia no calendário (ou dois cliques no mesmo dia) — altera só se a
                  hospedagem fica <strong>Ativado</strong> ou <strong>Desativado</strong> no calendário, de forma permanente no
                  combobox.
                  <br />
                  <strong>Período:</strong> dois cliques em dias diferentes; nesses dias vale o escolhido; depois volta ao padrão anterior.
                </p>
              </div>

              {form.periodosStatus.filter((p) => p.ate >= hojePeriodo).length > 0 && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Períodos configurados</label>
                  <ul className="space-y-1.5">
                    {form.periodosStatus
                      .map((p, i) => ({ p, i }))
                      .filter(({ p }) => p.ate >= hojePeriodo)
                      .map(({ p, i }) => (
                        <li
                          key={`${p.de}-${p.ate}-${p.tipo}-${i}`}
                          className="flex items-center justify-between py-2 px-3 rounded-lg bg-white border border-gray-200 text-sm"
                        >
                          <span className="font-medium text-gray-800">
                            De {p.de === p.ate ? p.de : `${p.de} até ${p.ate}`}
                            <span
                              className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                                p.tipo === 'desativado'
                                  ? 'bg-red-100 text-red-800'
                                  : p.tipo === 'ativado'
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : p.tipo === 'naviex'
                                      ? 'bg-violet-100 text-violet-800'
                                      : p.tipo === 'principal'
                                        ? 'bg-amber-100 text-amber-900'
                                        : 'bg-orange-100 text-orange-900'
                              }`}
                            >
                              {p.tipo === 'ativado'
                                ? 'Ativar'
                                : p.tipo === 'desativado'
                                  ? 'Desativado'
                                  : p.tipo === 'naviex'
                                    ? 'Destaque Naviex'
                                    : p.tipo === 'principal'
                                      ? 'Destaque Principal'
                                      : 'Destaque Categoria'}
                            </span>
                          </span>
                          <button
                            type="button"
                            onClick={() => removerPeriodoStatus(i)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Remover período"
                            aria-label="Remover"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </section>
          )}

          {/* Tab: Calendário */}
          {formTab === 'calendario' && (
            <div className="space-y-4 max-w-2xl">
              <p className="text-sm text-gray-600">
                Datas em <span className="text-red-600 font-medium">vermelho</span> = bloqueadas (manual ou
                Booking/Airbnb).
              </p>
              <div className="flex items-center gap-4 mb-4">
                <button
                  type="button"
                  onClick={() => {
                    if (calMes === 0) {
                      setCalAno((a) => a - 1);
                      setCalMes(11);
                    } else setCalMes((m) => m - 1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  ←
                </button>
                <span className="font-medium capitalize">{mesLabel}</span>
                <button
                  type="button"
                  onClick={() => {
                    if (calMes === 11) {
                      setCalAno((a) => a + 1);
                      setCalMes(0);
                    } else setCalMes((m) => m + 1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  →
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-gray-500 mb-2">
                {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((d) => (
                  <div key={d}>{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: startPad }, (_, i) => (
                  <div key={`pad-${i}`} className="aspect-square" />
                ))}
                {Array.from({ length: days }, (_, i) => {
                  const d = i + 1;
                  const dataStr = `${calAno}-${String(calMes + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                  const bloqueado = blockedDates.has(dataStr);
                  const isManual = !!form.bloqueiosManuais[dataStr];
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => toggleBloqueioManual(dataStr)}
                      className={`aspect-square rounded-lg text-sm font-medium border transition-colors ${
                        bloqueado
                          ? isManual
                            ? 'bg-red-200 border-red-300 text-red-800'
                            : 'bg-amber-100 border-amber-200 text-amber-800'
                          : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-800'
                      }`}
                      title={bloqueado ? (isManual ? 'Bloqueio manual - clique para desbloquear' : 'Bloqueado (iCal)') : 'Disponível - clique para bloquear'}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tab: iCal Sync */}
          {formTab === 'ical' && (
            <div className="space-y-4 max-w-2xl">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800 mb-4">
                <strong>Sincronização automática:</strong> O calendário é atualizado a cada 30 min enquanto o admin está aberto.
              </div>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                <p className="font-medium mb-2">Como obter as URLs iCal:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>
                    <strong>Booking.com</strong>: Extranet → Tarifas e disponibilidade → Sincronizar calendários →
                    Exportar
                  </li>
                  <li>
                    <strong>Airbnb</strong>: Calendário do anfitrião → Sincronizar calendários → Exportar calendário
                  </li>
                </ul>
                <p className="mt-2 text-amber-800">
                  A atualização pode levar 30 min a 3h. Clique em &quot;Sincronizar agora&quot; para buscar as
                  reservas e evitar overbooking.
                </p>
                {form.icalConfig.lastSyncAt && (
                  <p className="mt-2">
                    Última sync: {new Date(form.icalConfig.lastSyncAt).toLocaleString('pt-BR')} —{' '}
                    {form.icalConfig.blockedDatesFromIcal.length} data(s) bloqueada(s)
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">URLs iCal (Booking e/ou Airbnb)</label>
                <p className="text-xs text-gray-500 mb-2">
                  Adicione uma URL para cada quarto ou listing. Cada tipo de quarto no Booking/Airbnb pode ter seu próprio calendário.
                </p>
                <div className="space-y-2">
                  {form.icalConfig.icalUrls.map((u, i) => (
                    <div key={i} className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
                      <div className="flex gap-2">
                        <select
                          value={u.origem}
                          onChange={(e) => {
                            setForm((f) => ({
                              ...f,
                              icalConfig: {
                                ...f.icalConfig,
                                icalUrls: f.icalConfig.icalUrls.map((iu, j) =>
                                  j === i ? { ...iu, origem: e.target.value as 'booking' | 'airbnb' } : iu
                                ),
                              },
                            }));
                            setDirty(true);
                          }}
                          className="w-28 py-2 px-3 rounded border border-gray-300"
                        >
                          <option value="booking">Booking</option>
                          <option value="airbnb">Airbnb</option>
                        </select>
                        <input
                          type="text"
                          value={u.descricao ?? ''}
                          onChange={(e) => {
                            setForm((f) => ({
                              ...f,
                              icalConfig: {
                                ...f.icalConfig,
                                icalUrls: f.icalConfig.icalUrls.map((iu, j) =>
                                  j === i ? { ...iu, descricao: e.target.value } : iu
                                ),
                              },
                            }));
                            setDirty(true);
                          }}
                          placeholder="Ex: Quarto Duplo, Suíte"
                          className="flex-1 min-w-[120px] py-2 px-3 rounded border border-gray-300"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setForm((f) => ({
                              ...f,
                              icalConfig: {
                                ...f.icalConfig,
                                icalUrls: f.icalConfig.icalUrls.filter((_, j) => j !== i),
                              },
                            }));
                            setDirty(true);
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Remover"
                        >
                          ×
                        </button>
                      </div>
                      <input
                        type="url"
                        value={u.url}
                        onChange={(e) => {
                          setForm((f) => ({
                            ...f,
                            icalConfig: {
                              ...f.icalConfig,
                              icalUrls: f.icalConfig.icalUrls.map((iu, j) => (j === i ? { ...iu, url: e.target.value } : iu)),
                            },
                          }));
                          setDirty(true);
                        }}
                        placeholder="https://..."
                        className="w-full py-2 px-3 rounded border border-gray-300"
                      />
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setForm((f) => ({
                      ...f,
                      icalConfig: {
                        ...f.icalConfig,
                        icalUrls: [...f.icalConfig.icalUrls, { url: '', origem: 'booking', descricao: '' }],
                      },
                    }));
                    setDirty(true);
                  }}
                  className="mt-2 px-3 py-2 text-sm border border-orange-300 text-orange-600 rounded-lg hover:bg-orange-50"
                >
                  + Adicionar URL iCal
                </button>
              </div>
              <button
                type="button"
                onClick={() => void handleIcalSync()}
                disabled={
                  icalSyncLoading ||
                  !form.icalConfig.icalUrls.some((u) => u.url?.trim()?.startsWith('http'))
                }
                className="px-4 py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {icalSyncLoading ? 'Sincronizando...' : 'Sincronizar agora'}
              </button>
              <p className="text-xs text-gray-500">
                Se ocorrer erro de CORS, as URLs do Booking/Airbnb podem bloquear requisições do navegador. Nesse
                caso, crie um endpoint no backend que faça proxy do fetch.
              </p>
            </div>
          )}

          {/* Tab: Localização */}
          {formTab === 'localizacao' && (
            <div className="space-y-4 max-w-2xl">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Endereço</label>
                <input
                  type="text"
                  value={form.endereco}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, endereco: e.target.value }));
                    setDirty(true);
                  }}
                  className="w-full py-2 px-3 rounded-lg border border-gray-300"
                  placeholder="Endereço completo"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                  <input
                    type="number"
                    step="any"
                    value={form.lat ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((f) => ({ ...f, lat: v ? parseFloat(v) : undefined }));
                      setDirty(true);
                    }}
                    className="w-full py-2 px-3 rounded-lg border border-gray-300"
                    placeholder="-14.235"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                  <input
                    type="number"
                    step="any"
                    value={form.lng ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((f) => ({ ...f, lng: v ? parseFloat(v) : undefined }));
                      setDirty(true);
                    }}
                    className="w-full py-2 px-3 rounded-lg border border-gray-300"
                    placeholder="-51.9253"
                  />
                </div>
              </div>
              <p className="text-sm text-gray-500">
                Cole o link do Google Maps para extrair coordenadas automaticamente (em desenvolvimento) ou informe
                manualmente.
              </p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
